import { ArrowLeft } from "lucide-react";
import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface HeaderSlots {
  titleEl: HTMLElement | null;
  actionsEl: HTMLElement | null;
  /**
   * Supplied once by AppShell. Kept in the context rather than taken as a prop
   * so no page has to thread a router through to render its own header.
   */
  navigate?: (to: string) => void;
}

const SlotContext = createContext<HeaderSlots>({ titleEl: null, actionsEl: null });

export function HeaderSlotProvider({
  slots,
  navigate,
  children,
}: {
  slots: Omit<HeaderSlots, "navigate">;
  navigate?: (to: string) => void;
  children: ReactNode;
}) {
  const value = navigate ? { ...slots, navigate } : slots;
  return <SlotContext.Provider value={value}>{children}</SlotContext.Provider>;
}

/** Used by the shell to register its two portal targets in the top bar. */
export function useHeaderSlotRefs() {
  const [titleEl, setTitleEl] = useState<HTMLElement | null>(null);
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  return { slots: { titleEl, actionsEl }, setTitleEl, setActionsEl };
}

/**
 * Where a page sits under a list, so the header can offer a way back.
 *
 * Detail screens used to each render their own back button into `actions`, on
 * the right of the bar among Save and Delete. Fifteen of them, each with its
 * own label, icon size and destination. Declaring the parent instead puts one
 * arrow and one breadcrumb on the left of every detail page, and leaves the
 * right side for things that act on the record.
 */
export interface PageParent {
  /** Where "up" goes, e.g. `/products`. */
  to: string;
  /** Already translated, e.g. `t("navigation.item.products")`. */
  label: string;
  /**
   * Asked before leaving. Return false to stay — that is where an unsaved-work
   * confirmation belongs. Applies to the arrow and the breadcrumb alike, so
   * neither can slip past the guard.
   */
  beforeLeave?: () => boolean | Promise<boolean>;
}

export interface PageHeaderProps {
  title: string;
  /** Text, or text with links (a supplier on an order). */
  subtitle?: ReactNode;
  /** Counts, status pills — anything that belongs beside the title. */
  badges?: ReactNode;
  /** The page's own menu, rendered on the right of the top bar. */
  actions?: ReactNode;
  /**
   * The list this record belongs to. Supplying it turns the title into
   * `Parent / Title` with the parent clickable, and puts a back arrow ahead of
   * it. Omit on the main navigation screens: they are the top of the tree and
   * have nowhere to go back to.
   */
  parent?: PageParent;
  /** Overrides the shell's navigate. Only needed outside AppShell. */
  onNavigate?: (to: string) => void;
}

/**
 * Publishes a page's title, subtitle, badges and menu into the shell's top bar.
 *
 * It portals rather than pushing into shared state: the content is just
 * children, so it re-renders with the page and there is no effect-dependency
 * dance to keep an object in sync. A page that renders no `<PageHeader/>`
 * simply leaves the bar empty.
 *
 *   <PageHeader title={t("navigation.item.users")} actions={<Button…/>} />
 */
export function PageHeader({
  title,
  subtitle,
  badges,
  actions,
  parent,
  onNavigate,
}: PageHeaderProps) {
  const { titleEl, actionsEl, navigate: navigateFromShell } = useContext(SlotContext);
  const { t } = useTranslation();

  const go = onNavigate ?? navigateFromShell;
  const leave = async () => {
    if (!parent || !go) return;
    if (parent.beforeLeave && !(await parent.beforeLeave())) return;
    go(parent.to);
  };

  return (
    <>
      {titleEl &&
        createPortal(
          // When the bar runs out of room the parent crumb truncates first,
          // the title keeps enough to be read, and badges are clipped at the
          // end — nothing is allowed to paint over anything else.
          <div className="flex min-w-0 items-center gap-2.5 overflow-hidden">
            {/* The title keeps up to 60% of the bar before the badges give way:
                with equal shrink a row of badges cut "030-0012" to "030-00…". */}
            <div className="min-w-[9rem] max-w-[60%] shrink-0">
              <h1 className="flex min-w-0 items-center gap-1.5 overflow-hidden text-[15px] font-semibold leading-tight text-ink">
                {parent && go && (
                  <>
                    {/* The arrow lives inside the crumb rather than beside it.
                        As its own icon button it sat next to the sidebar
                        toggle, and two adjacent icon buttons in the same corner
                        read as a pair of window controls: you could not tell
                        which one left the page. One control, carrying both the
                        familiar arrow and the name of where it goes, cannot be
                        confused with the chrome around it. */}
                    <button
                      type="button"
                      onClick={() => void leave()}
                      title={t("navigation.back-to", "Back to {{parent}}", { parent: parent.label })}
                      className="group -ml-1 flex min-w-8 items-center gap-1 [flex-shrink:6] rounded-md px-1 py-0.5 font-medium text-ink-muted hover:bg-surface-sunk hover:text-accent focus-visible:outline-2 focus-visible:outline-accent"
                    >
                      <ArrowLeft className="size-3.5 shrink-0" />
                      <span className="truncate">{parent.label}</span>
                    </button>
                    <span aria-hidden="true" className="shrink-0 text-ink-faint">
                      /
                    </span>
                  </>
                )}
                <span className="min-w-0 truncate">{title}</span>
              </h1>
              {subtitle && (
                <p className="truncate text-[12px] leading-tight text-ink-muted">{subtitle}</p>
              )}
            </div>
            {badges && (
              <div className="flex min-w-0 shrink items-center gap-1.5 overflow-hidden [&>*]:shrink-0">{badges}</div>
            )}
          </div>,
          titleEl,
        )}

      {actionsEl && createPortal(<>{actions}</>, actionsEl)}
    </>
  );
}
